import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { persistLanguageChoice, type PortalUserType } from '@/lib/languagePreference';

const LANGUAGES = [
  { code: 'en', label: 'EN', fullLabel: 'English', native: 'English' },
  { code: 'ja', label: 'JP', fullLabel: '日本語', native: '日本語' },
  { code: 'zh', label: '中文', fullLabel: '中文', native: '中文' },
];

interface LanguageSwitcherProps {
  compact?: boolean;
  onOpenChange?: (open: boolean) => void;
  userType?: PortalUserType;
  profileId?: string;
}

export default function LanguageSwitcher({ compact = false, onOpenChange, userType, profileId }: LanguageSwitcherProps) {
  const { i18n: i18nInstance, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width: 1023px)');

  const currentLang = LANGUAGES.find((l) => l.code === i18nInstance.language) || LANGUAGES[0];

  const handleChange = (code: string) => {
    persistLanguageChoice(code, userType, profileId);
    setOpen(false);
    onOpenChange?.(false);
  };

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    onOpenChange?.(next);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        onOpenChange?.(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onOpenChange]);

  // Mobile bottom sheet
  if (isMobile) {
    return (
      <div ref={ref} className="relative">
        <button
          onClick={handleToggle}
          className={
            compact
              ? 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap'
              : 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-[#374151] text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap'
          }
        >
          <i className="ri-translate-2 text-sm"></i>
          <span>{currentLang.label}</span>
          <i className={open ? 'ri-arrow-up-s-line text-xs' : 'ri-arrow-down-s-line text-xs'}></i>
        </button>

        {open && (
          <>
            {/* Dark backdrop */}
            <div
              className="fixed inset-0 z-[9999] bg-black/50"
              onClick={() => { setOpen(false); onOpenChange?.(false); }}
            />
            {/* Bottom sheet */}
            <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white rounded-t-2xl overflow-hidden" style={{ boxShadow: '0 -4px 24px rgba(0,0,0,0.15)' }}>
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-4 pb-3">
                <span className="text-[#1A1A1A] text-base font-bold">{t('lang.label')}</span>
                <button
                  onClick={() => { setOpen(false); onOpenChange?.(false); }}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 cursor-pointer"
                >
                  <i className="ri-close-line text-[#374151] text-lg"></i>
                </button>
              </div>
              {/* Options */}
              <div className="px-4 pb-6 space-y-1.5">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleChange(lang.code)}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium cursor-pointer transition-colors min-h-[52px] ${
                      lang.code === i18nInstance.language
                        ? 'bg-[#FEF0E7] text-[#E8621A]'
                        : 'text-[#374151] hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-bold w-8 text-center text-base">{lang.label}</span>
                    <span className="text-sm">{lang.native}</span>
                    {lang.code === i18nInstance.language && (
                      <i className="ri-check-line ml-auto text-[#E8621A] text-lg"></i>
                    )}
                  </button>
                ))}
              </div>
              {/* Safe area for iPhone */}
              <div className="h-[env(safe-area-inset-bottom)]"></div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Desktop dropdown (unchanged behavior)
  if (compact) {
    return (
      <div ref={ref} className="relative">
        <button
          onClick={handleToggle}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap"
        >
          <i className="ri-translate-2 text-sm"></i>
          <span>{currentLang.label}</span>
          <i className={open ? 'ri-arrow-up-s-line text-xs' : 'ri-arrow-down-s-line text-xs'}></i>
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); onOpenChange?.(false); }}></div>
            <div className="absolute bottom-full mb-2 left-0 z-50 bg-[#2A2A2A] border border-white/10 rounded-xl overflow-hidden min-w-[120px]">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleChange(lang.code)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                    lang.code === i18nInstance.language
                      ? 'bg-[#E8621A]/20 text-[#E8621A]'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <span className="font-bold w-6 text-center">{lang.label}</span>
                  <span>{lang.fullLabel}</span>
                  {lang.code === i18nInstance.language && (
                    <i className="ri-check-line ml-auto text-[#E8621A]"></i>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // Desktop header variant (light background)
  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleToggle}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-[#374151] text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap"
      >
        <i className="ri-translate-2 text-sm text-[#374151]"></i>
        <span>{currentLang.label}</span>
        <i className={open ? 'ri-arrow-up-s-line text-xs' : 'ri-arrow-down-s-line text-xs'}></i>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); onOpenChange?.(false); }}></div>
          <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-gray-100 rounded-xl overflow-hidden min-w-[140px]" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleChange(lang.code)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                  lang.code === i18nInstance.language
                    ? 'bg-[#FEF0E7] text-[#E8621A]'
                    : 'text-[#374151] hover:bg-gray-50'
                }`}
              >
                <span className="font-bold w-6 text-center">{lang.label}</span>
                <span>{lang.fullLabel}</span>
                {lang.code === i18nInstance.language && (
                  <i className="ri-check-line ml-auto text-[#E8621A]"></i>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}